/**
 * Industry Update Workbench
 *
 * Full-screen drawer panel analysts use to create or edit an industry update.
 * Three regions:
 *   - Left:   Generate (grounded AI + metadata form)
 *   - Middle: Markdown editor + live preview (split vertically)
 *   - Right:  Sources / Images tabs
 *
 * The workbench is the source of truth for the in-flight draft. `onSave` is
 * called with a fully-assembled payload; the parent decides create-vs-update.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertTriangle, BookOpen, Check, ExternalLink, Eye, Globe, Image as ImageIcon,
  Loader2, Plus, Shield, Sparkles, Trash2, TrendingUp, Upload, X,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import type { BadgeVariant } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import {
  draftIndustryUpdate,
  getCategoryLabel,
  getSeverityVariant,
  uploadIndustryUpdateImage,
  verifyIndustryUpdateSource,
} from '../../services/industryUpdates';
import type {
  IndustryCategory,
  IndustrySeverity,
  IndustryUpdate,
  IndustryUpdateImage,
  IndustryUpdateSource,
  Tenant,
} from '../../data/types';
import { cn } from '../../lib/utils';
import { CANONICAL_INDUSTRIES } from '../../lib/industries';

// ---------------------------------------------------------------------------

const CATEGORIES: Array<{ value: IndustryCategory; label: string; icon: React.ReactNode }> = [
  { value: 'patch_alert', label: 'Patch Alert', icon: <Shield className="w-4 h-4" /> },
  { value: 'trend_analysis', label: 'Trend Analysis', icon: <TrendingUp className="w-4 h-4" /> },
  { value: 'global_context', label: 'Global Context', icon: <Globe className="w-4 h-4" /> },
  { value: 'best_practice', label: 'Best Practice', icon: <BookOpen className="w-4 h-4" /> },
];

const SEVERITIES: IndustrySeverity[] = ['critical', 'high', 'medium', 'low', 'informational'];

// ---------------------------------------------------------------------------

export interface WorkbenchPayload {
  tenantId: string | null;
  title: string;
  content: string;
  category: IndustryCategory;
  severity: IndustrySeverity | null;
  /** Publication date. Purely descriptive — nothing expires on it. */
  effectiveFrom: string;
  industries: string[] | null;
  sources: IndustryUpdateSource[];
  images: IndustryUpdateImage[];
  searchQuery: string | null;
  generationModel: string | null;
}

interface UpdateWorkbenchProps {
  isOpen: boolean;
  onClose: () => void;
  editing: IndustryUpdate | null;       // null = create
  tenants: Tenant[];
  onSave: (payload: WorkbenchPayload, updateId: string | null) => Promise<void>;
}

// ---------------------------------------------------------------------------

function severityBorderClass(severity: IndustrySeverity | string | null): string {
  switch (severity) {
    case 'critical': return 'border-l-red-500';
    case 'high': return 'border-l-orange-500';
    case 'medium': return 'border-l-yellow-500';
    case 'low': return 'border-l-green-500';
    case 'informational': return 'border-l-blue-500';
    default: return 'border-l-slate-400';
  }
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

function toDateInput(iso: string | null | undefined): string {
  return iso ? iso.split('T')[0] : '';
}

// ---------------------------------------------------------------------------

export function UpdateWorkbench({ isOpen, onClose, editing, tenants, onSave }: UpdateWorkbenchProps) {
  // --- Form state ---------------------------------------------------------
  const [tenantId, setTenantId] = useState<string | null>(editing?.tenantId ?? null);
  const [title, setTitle] = useState(editing?.title ?? '');
  const [content, setContent] = useState(editing?.content ?? '');
  const [category, setCategory] = useState<IndustryCategory>(editing?.category ?? 'patch_alert');
  const [severity, setSeverity] = useState<IndustrySeverity | null>(
    editing ? editing.severity : 'medium'
  );
  const [effectiveFrom, setEffectiveFrom] = useState<string>(toDateInput(editing?.effectiveFrom) || todayIso());
  // Updates do not expire. effectiveUntil is a legacy column: nothing reads it
  // any more (report inclusion is by analyst selection, and the customer feed
  // shows every update that has not been retracted), so the workbench neither
  // shows nor sets it. Editing an old row that has one leaves it untouched.

  const [sources, setSources] = useState<IndustryUpdateSource[]>(editing?.sources ?? []);
  const [images, setImages] = useState<IndustryUpdateImage[]>(editing?.images ?? []);
  const [searchQuery, setSearchQuery] = useState<string>(editing?.searchQuery ?? '');
  const [generationModel, setGenerationModel] = useState<string | null>(editing?.generationModel ?? null);
  const [contextHint, setContextHint] = useState('');
  const [industries, setIndustries] = useState<string[]>(editing?.industries ?? []);

  const toggleIndustry = useCallback((value: string) => {
    setIndustries(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  }, []);

  // Severity only applies to patch_alert. Switching away from it clears severity
  // so the saved record matches what the UI shows.
  const handleCategoryChange = useCallback((next: IndustryCategory) => {
    setCategory(next);
    if (next !== 'patch_alert') setSeverity(null);
  }, []);

  // --- UI state -----------------------------------------------------------
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genWarnings, setGenWarnings] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [previewOnly, setPreviewOnly] = useState(false);
  const contentRef = useRef<HTMLTextAreaElement | null>(null);

  // -----------------------------------------------------------------------

  const insertAtCursor = useCallback((text: string) => {
    const el = contentRef.current;
    if (!el) {
      setContent(prev => prev + text);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + text + el.value.slice(end);
    setContent(next);
    // restore cursor just after inserted text
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + text.length, start + text.length);
    });
  }, []);

  // --- Generation ---------------------------------------------------------

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    setGenWarnings([]);
    const res = await draftIndustryUpdate({
      category,
      context: contextHint.trim() || undefined,
      searchQuery: searchQuery.trim() || undefined,
    });
    setGenerating(false);
    if (!res.success) {
      setGenError(res.error || 'Generation failed');
      return;
    }
    setTitle(res.data.title);
    setContent(res.data.content);
    if (res.data.severity && category === 'patch_alert') setSeverity(res.data.severity as IndustrySeverity);
    if (res.data.searchQuery) setSearchQuery(res.data.searchQuery);
    if (res.data.generationModel) setGenerationModel(res.data.generationModel);
    if (res.data.warnings && res.data.warnings.length > 0) setGenWarnings(res.data.warnings);
    if (res.data.sources && res.data.sources.length > 0) {
      // Merge: keep any analyst-added sources; append AI ones that aren't already in the list.
      setSources(prev => {
        const existing = new Set(prev.map(s => s.url));
        const merged = [...prev];
        for (const s of res.data.sources!) {
          if (!existing.has(s.url)) merged.push({ ...s, verifyStatus: 'unknown' });
        }
        return merged;
      });
    }
  }, [category, contextHint, searchQuery]);

  // --- Source actions -----------------------------------------------------

  const [newSourceUrl, setNewSourceUrl] = useState('');
  const handleAddSource = useCallback(() => {
    const url = newSourceUrl.trim();
    if (!url) return;
    setSources(prev => [...prev, { url, title: url, verifyStatus: 'unknown' }]);
    setNewSourceUrl('');
  }, [newSourceUrl]);

  const handleRemoveSource = useCallback((idx: number) => {
    setSources(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleVerifySource = useCallback(async (idx: number) => {
    const source = sources[idx];
    if (!source) return;
    setSources(prev => prev.map((s, i) => i === idx ? { ...s, verifyStatus: 'unknown' } : s));
    const res = await verifyIndustryUpdateSource(source.url);
    if (!res.success) {
      setSources(prev => prev.map((s, i) => i === idx ? { ...s, verifyStatus: 'failed' } : s));
      return;
    }
    setSources(prev => prev.map((s, i) => i === idx ? {
      ...s,
      title: res.data.title ?? s.title,
      verifyStatus: res.data.ok ? 'ok' : 'failed',
      verifyHttpStatus: res.data.status,
      verifiedAt: res.data.verifiedAt,
    } : s));
  }, [sources]);

  const handleCiteSource = useCallback((idx: number) => {
    insertAtCursor(` [${idx + 1}]`);
  }, [insertAtCursor]);

  // --- Image actions ------------------------------------------------------

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUploadImage = useCallback(async (file: File) => {
    setUploadError(null);
    setUploading(true);
    try {
      const uploaded = await uploadIndustryUpdateImage(file);
      setImages(prev => [...prev, {
        blobPath: uploaded.blobPath,
        url: uploaded.url,
        caption: '',
        altText: file.name.replace(/\.[^.]+$/, ''),
        uploadedAt: new Date().toISOString(),
      }]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleRemoveImage = useCallback((idx: number) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleInsertImage = useCallback((idx: number) => {
    const img = images[idx];
    if (!img) return;
    insertAtCursor(`\n\n![${img.altText ?? ''}](${img.url})\n\n`);
  }, [images, insertAtCursor]);

  // --- Save ---------------------------------------------------------------

  const validate = (): string | null => {
    if (!title.trim()) return 'Title is required';
    if (!content.trim()) return 'Content is required';
    if (!effectiveFrom) return 'Effective from date is required';
    return null;
  };

  const handleSave = useCallback(async () => {
    const err = validate();
    if (err) {
      setGenError(err);
      return;
    }
    setSaving(true);
    try {
      await onSave({
        tenantId,
        title: title.trim(),
        content,
        category,
        severity: category === 'patch_alert' ? severity : null,
        effectiveFrom,
        industries: industries.length === 0 ? null : industries,
        sources,
        images,
        searchQuery: searchQuery || null,
        generationModel,
      }, editing?.updateId ?? null);
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, title, content, category, severity, effectiveFrom, industries, sources, images, searchQuery, generationModel, editing, onSave]);

  // --- Rendered preview ---------------------------------------------------

  const preview = useMemo(() => (
    <Card className={cn('border-l-4', severityBorderClass(severity))}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="font-medium text-text-primary">{title || 'Untitled'}</h3>
          {severity && (
            <Badge variant={getSeverityVariant(severity) as BadgeVariant} className="capitalize">
              {severity}
            </Badge>
          )}
          <Badge variant="outline">{getCategoryLabel(category)}</Badge>
        </div>
        {images.length > 0 && (
          <img
            src={images[0].url}
            alt={images[0].altText ?? ''}
            className="rounded-lg mb-3 max-h-56 object-cover w-full"
          />
        )}
        <div className="prose prose-sm dark:prose-invert max-w-none text-text-secondary">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || '_No content yet._'}</ReactMarkdown>
        </div>
        {sources.length > 0 && (
          <div className="mt-4 pt-3 border-t border-border-default">
            <div className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Sources</div>
            <ol className="text-xs text-text-secondary space-y-1 list-decimal pl-4">
              {sources.map((s, i) => (
                <li key={`${s.url}-${i}`}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline break-all">
                    {s.title || s.url}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  ), [title, severity, category, images, content, sources]);

  // -----------------------------------------------------------------------

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative ml-auto h-full w-full max-w-[1400px] bg-bg-primary border-l border-border-default shadow-2xl flex flex-col">
        {/* Header */}
        <div className="border-b border-border-default px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-text-primary">
              {editing ? 'Edit Industry Update' : 'New Industry Update'}
            </h2>
            {generationModel && (
              <Badge variant="outline" className="text-xs">
                <Sparkles className="w-3 h-3 mr-1" /> {generationModel}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={previewOnly ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setPreviewOnly(p => !p)}
            >
              <Eye className="w-4 h-4 mr-1" />
              {previewOnly ? 'Exit Preview' : 'Preview'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        {previewOnly ? (
          <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full">
            {preview}
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-[320px_1fr_360px] min-h-0">
            {/* ---------------------------------------------------------- */}
            {/* LEFT: Generate + Metadata                                  */}
            {/* ---------------------------------------------------------- */}
            <div className="border-r border-border-default overflow-y-auto p-4 space-y-5">
              {/* Generate box */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-accent-blue" />
                  <h3 className="text-sm font-semibold text-text-primary">Generate with web search</h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Category</label>
                    <div className="grid grid-cols-2 gap-1">
                      {CATEGORIES.map(c => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => handleCategoryChange(c.value)}
                          className={cn(
                            'flex items-center gap-1 px-2 py-1.5 rounded text-xs border transition-colors',
                            category === c.value
                              ? 'bg-accent-blue/10 border-accent-blue text-accent-blue'
                              : 'bg-bg-secondary border-border-default text-text-secondary hover:border-text-muted'
                          )}
                        >
                          {c.icon}
                          <span className="truncate">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Search query <span className="text-text-muted">(optional, will auto-derive)</span>
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="e.g. ransomware targeting Australian healthcare April 2026"
                      className="w-full px-2 py-1.5 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Extra context</label>
                    <textarea
                      value={contextHint}
                      onChange={(e) => setContextHint(e.target.value)}
                      rows={2}
                      placeholder="e.g. focus on mitigations relevant to finance sector"
                      className="w-full px-2 py-1.5 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue resize-none"
                    />
                  </div>

                  <Button onClick={handleGenerate} disabled={generating} className="w-full">
                    {generating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching web &amp; drafting…</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate</>
                    )}
                  </Button>
                  {genError && (
                    <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-500">
                      {genError}
                    </div>
                  )}
                  {genWarnings.length > 0 && (
                    <div className="p-2 bg-yellow-500/10 border border-yellow-500/40 rounded text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                      <div className="font-semibold flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Verify before publishing
                      </div>
                      {genWarnings.map((w, i) => (
                        <div key={i}>• {w}</div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* Metadata */}
              <section className="space-y-3 pt-4 border-t border-border-default">
                <h3 className="text-sm font-semibold text-text-primary">Metadata</h3>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-2 py-1.5 bg-bg-secondary border border-border-default rounded text-sm focus:outline-none focus:border-accent-blue"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Tenant</label>
                  <select
                    value={tenantId ?? ''}
                    onChange={(e) => setTenantId(e.target.value || null)}
                    className="w-full px-2 py-1.5 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue"
                  >
                    <option value="">Global (all tenants)</option>
                    {tenants.map(t => (
                      <option key={t.tenantId} value={t.tenantId}>{t.displayName}</option>
                    ))}
                  </select>
                </div>
                {tenantId === null && (
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Industries{' '}
                      <span className="text-text-muted">
                        ({industries.length === 0 ? 'all' : `${industries.length} selected`})
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {CANONICAL_INDUSTRIES.map(opt => {
                        const selected = industries.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleIndustry(opt.value)}
                            className={cn(
                              'px-2 py-0.5 rounded-full text-[11px] border transition-colors',
                              selected
                                ? 'bg-accent-blue/10 border-accent-blue text-accent-blue'
                                : 'bg-bg-secondary border-border-default text-text-secondary hover:border-text-muted'
                            )}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-text-muted mt-1">
                      Empty = visible to all industries. Tenant-specific updates ignore this filter.
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {category === 'patch_alert' && (
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Severity</label>
                      <select
                        value={severity ?? ''}
                        onChange={(e) => setSeverity((e.target.value as IndustrySeverity) || null)}
                        className="w-full px-2 py-1.5 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue"
                      >
                        <option value="">None</option>
                        {SEVERITIES.map(s => (
                          <option key={s} value={s} className="capitalize">{s}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className={category === 'patch_alert' ? '' : 'col-span-2'}>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Published</label>
                    <input
                      type="date"
                      value={effectiveFrom}
                      onChange={(e) => setEffectiveFrom(e.target.value)}
                      className="w-full px-2 py-1.5 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue"
                    />
                  </div>
                </div>
              </section>
            </div>

            {/* ---------------------------------------------------------- */}
            {/* MIDDLE: Editor + Preview (split vertically)                */}
            {/* ---------------------------------------------------------- */}
            <div className="grid grid-rows-2 min-h-0">
              <div className="border-b border-border-default flex flex-col min-h-0">
                <div className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide border-b border-border-default bg-bg-secondary/40">
                  Markdown source
                </div>
                <textarea
                  ref={contentRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write the update here. Supports markdown and inline [1] citations."
                  className="flex-1 w-full px-4 py-3 bg-bg-primary text-text-primary text-sm focus:outline-none resize-none font-mono"
                />
              </div>
              <div className="flex flex-col min-h-0">
                <div className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide border-b border-border-default bg-bg-secondary/40">
                  Live preview
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {preview}
                </div>
              </div>
            </div>

            {/* ---------------------------------------------------------- */}
            {/* RIGHT: Sources / Images tabs                               */}
            {/* ---------------------------------------------------------- */}
            <div className="border-l border-border-default flex flex-col min-h-0">
              <Tabs defaultValue="sources" className="flex flex-col min-h-0 h-full">
                <TabsList className="px-2 pt-2">
                  <TabsTrigger value="sources">Sources ({sources.length})</TabsTrigger>
                  <TabsTrigger value="images">Images ({images.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="sources" className="flex-1 overflow-y-auto p-3 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newSourceUrl}
                      onChange={(e) => setNewSourceUrl(e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-2 py-1.5 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue"
                    />
                    <Button size="sm" onClick={handleAddSource} disabled={!newSourceUrl.trim()}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>

                  {sources.length === 0 && (
                    <div className="text-xs text-text-muted py-6 text-center">
                      No sources yet. Generate with web search or add manually.
                    </div>
                  )}

                  {sources.map((s, i) => (
                    <Card key={`${s.url}-${i}`} className="text-xs">
                      <CardContent className="p-2 space-y-1">
                        <div className="flex items-start gap-1">
                          <span className="font-mono text-text-muted mt-0.5">[{i + 1}]</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-text-primary truncate">{s.title || s.url}</div>
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-blue hover:underline break-all text-[11px] flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{s.url}</span>
                            </a>
                            {s.snippet && (
                              <div className="text-text-muted mt-1 line-clamp-2">{s.snippet}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-1 pt-1 border-t border-border-default">
                          <div className="flex items-center gap-1">
                            {s.verifyStatus === 'ok' && (
                              <Badge variant="green" className="text-[10px]"><Check className="w-3 h-3 mr-0.5" />Verified</Badge>
                            )}
                            {s.verifyStatus === 'failed' && (
                              <Badge variant="red" className="text-[10px]">Unreachable</Badge>
                            )}
                            {(!s.verifyStatus || s.verifyStatus === 'unknown') && (
                              <span className="text-text-muted text-[10px]">Unverified</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleVerifySource(i)} title="Verify">
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleCiteSource(i)} title="Insert citation">
                              [{i + 1}]
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveSource(i)} title="Remove">
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="images" className="flex-1 overflow-y-auto p-3 space-y-2">
                  <label className="flex flex-col items-center gap-2 px-3 py-6 border-2 border-dashed border-border-default rounded-lg cursor-pointer hover:border-accent-blue hover:bg-accent-blue/5 transition-colors">
                    {uploading ? (
                      <Loader2 className="w-5 h-5 text-accent-blue animate-spin" />
                    ) : (
                      <Upload className="w-5 h-5 text-text-muted" />
                    )}
                    <span className="text-xs text-text-secondary">
                      {uploading ? 'Uploading…' : 'Click or drop an image'}
                    </span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadImage(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {uploadError && (
                    <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-500">
                      {uploadError}
                    </div>
                  )}

                  {images.length === 0 && !uploading && (
                    <div className="text-xs text-text-muted py-2 text-center">
                      No images attached.
                    </div>
                  )}

                  {images.map((img, i) => (
                    <Card key={`${img.url}-${i}`}>
                      <CardContent className="p-2 space-y-2">
                        <div className="flex gap-2">
                          <img src={img.url} alt={img.altText ?? ''} className="w-16 h-16 rounded object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0 space-y-1">
                            <input
                              type="text"
                              value={img.caption ?? ''}
                              onChange={(e) => setImages(prev => prev.map((p, idx) => idx === i ? { ...p, caption: e.target.value } : p))}
                              placeholder="Caption"
                              className="w-full px-2 py-1 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue"
                            />
                            <input
                              type="text"
                              value={img.altText ?? ''}
                              onChange={(e) => setImages(prev => prev.map((p, idx) => idx === i ? { ...p, altText: e.target.value } : p))}
                              placeholder="Alt text"
                              className="w-full px-2 py-1 bg-bg-secondary border border-border-default rounded text-xs focus:outline-none focus:border-accent-blue"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleInsertImage(i)} title="Insert in content">
                            <ImageIcon className="w-3 h-3 mr-1" /> Insert
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveImage(i)} title="Remove">
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-border-default px-6 py-3 flex items-center justify-between">
          <div className="text-xs text-text-muted">
            {sources.length > 0 && <>{sources.length} source{sources.length === 1 ? '' : 's'}</>}
            {sources.length > 0 && images.length > 0 && <> · </>}
            {images.length > 0 && <>{images.length} image{images.length === 1 ? '' : 's'}</>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save changes' : 'Create update'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
